// Phase 3 — Admin landing redirect.
// Every admin surface now lives under `/admin/{store-slug}/*`. When a user
// hits a bare `/admin` (e.g. right after login or via a legacy bookmark),
// we resolve the correct slug and forward them:
//
//   • Cashier / Packer with `store_slug` in profile → their own store's POS.
//   • Store Admin / Owner / Manager → their store's dashboard.
//   • Super Admin (no fixed store) → first available store's dashboard.
//     If they haven't provisioned any store yet, they land on `/admin/platform/dashboard`
//     which the AppShell tolerates as a synthetic slug (SA-only) rendering the
//     Platform Overview with no per-store scope.
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, canPOS } from "@/contexts/AuthContext";

const PLATFORM_SLUG = "platform"; // synthetic slug for SA without a selected store

export default function AdminLandingRedirect() {
  const { user } = useAuth();
  const [slug, setSlug] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user) return;
      // Store users → their own store slug is the only valid landing.
      if (user.role !== "SUPER_ADMIN") {
        if (alive) setSlug(user.store_slug || null);
        return;
      }
      // Super Admin → default to the synthetic `platform` slug (Platform
      // Overview). SA can still drill into any store from Stores → Open Store.
      if (alive) setSlug(PLATFORM_SLUG);
    })();
    return () => { alive = false; };
  }, [user]);

  if (!user) return <Navigate to="/login" replace />;
  if (slug == null && !error) return null; // still resolving

  const targetSlug = slug || PLATFORM_SLUG;
  const dest = canPOS(user) && user.role === "CASHIER"
    ? `/admin/${targetSlug}/pos`
    : `/admin/${targetSlug}/dashboard`;
  return <Navigate to={dest} replace />;
}
